/**
 * useWlRecurrenceOptions — Hooks de opções de periodicidade (plano e add-on)
 *
 * Encapsula as RPCs `wl_list_plan_recurrence_options`,
 * `wl_upsert_plan_recurrence_option`, `wl_delete_plan_recurrence_option`,
 * `wl_toggle_plan_recurrence_option_active` e suas variantes para add-on.
 *
 * Também expõe `useWlMasterRecurringServices(periodicidade?)` para alimentar
 * o <Select> de serviços recorrentes do tenant master no editor.
 *
 * Todas as chamadas passam por RPC SECURITY DEFINER + wl_assert_permission.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("useWlRecurrenceOptions");

// ---- Tipos compartilhados ---------------------------------------------------
export type RecurrencePeriod =
  | "mensal"
  | "bimestral"
  | "trimestral"
  | "semestral"
  | "anual";

export interface WlRecurrenceOption {
  id: string;
  recurrence_interval: RecurrencePeriod;
  uaizy_master_service_id: string;
  service_name: string | null;
  service_recurrence: string | null;
  service_price: number | null;
  price: number;
  setup_fee: number;
  discount_percent: number | null;
  stripe_price_id: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface WlMasterRecurringService {
  id: string;
  name: string;
  description: string | null;
  recurrence_interval: string;
  recurring_price: number | null;
  currency: string;
  is_active: boolean;
}

// =============================================================================
// Lista serviços recorrentes do tenant master filtrável por periodicidade.
// =============================================================================
export function useWlMasterRecurringServices(period?: RecurrencePeriod | null) {
  return useQuery({
    queryKey: ["wl-master-recurring-services", period ?? null],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<WlMasterRecurringService[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_master_recurring_services" as never,
        {
          p_recurrence_interval: period ?? null,
          p_search: null,
        } as never,
      );
      if (error) {
        log.error("Erro ao listar serviços recorrentes", { error });
        throw error;
      }
      return (data || []) as WlMasterRecurringService[];
    },
  });
}

// =============================================================================
// Opções de periodicidade por PLANO
// =============================================================================
export function useWlPlanRecurrenceOptions(planId: string | null) {
  return useQuery({
    queryKey: ["wl-plan-recurrence-options", planId],
    enabled: !!planId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<WlRecurrenceOption[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_plan_recurrence_options" as never,
        { p_plan_id: planId } as never,
      );
      if (error) throw error;
      return ((data || []) as any[]).map((o) => ({
        id: o.id,
        recurrence_interval: o.recurrence_interval,
        uaizy_master_service_id: o.uaizy_master_service_id,
        service_name: o.service_name,
        service_recurrence: o.service_recurrence,
        service_price: o.service_price !== null ? Number(o.service_price) : null,
        price: Number(o.price) || 0,
        setup_fee: Number(o.setup_fee) || 0,
        discount_percent:
          o.discount_percent !== null ? Number(o.discount_percent) : null,
        stripe_price_id: o.stripe_price_id,
        is_active: !!o.is_active,
        sort_order: Number(o.sort_order) || 0,
      }));
    },
  });
}

export function useWlPlanRecurrenceMutations(planId: string | null) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["wl-plan-recurrence-options", planId] });

  const upsert = useMutation({
    mutationFn: async (input: {
      id?: string | null;
      payload: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc(
        "wl_upsert_plan_recurrence_option" as never,
        { p_payload: input.payload, p_id: input.id ?? null } as never,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc(
        "wl_delete_plan_recurrence_option" as never,
        { p_id: id } as never,
      );
      if (error) throw error;
      return true;
    },
    onSuccess: invalidate,
  });

  const toggleActive = useMutation({
    mutationFn: async (input: { id: string; active: boolean }) => {
      const { error } = await supabase.rpc(
        "wl_toggle_plan_recurrence_option_active" as never,
        { p_id: input.id, p_active: input.active } as never,
      );
      if (error) throw error;
      return input.active;
    },
    onSuccess: invalidate,
  });

  return { upsert, remove, toggleActive };
}

// =============================================================================
// Opções de periodicidade por ADD-ON
// =============================================================================
export function useWlAddonRecurrenceOptions(addonId: string | null) {
  return useQuery({
    queryKey: ["wl-addon-recurrence-options", addonId],
    enabled: !!addonId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<WlRecurrenceOption[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_addon_recurrence_options" as never,
        { p_addon_id: addonId } as never,
      );
      if (error) throw error;
      return ((data || []) as any[]).map((o) => ({
        id: o.id,
        recurrence_interval: o.recurrence_interval,
        uaizy_master_service_id: o.uaizy_master_service_id,
        service_name: o.service_name,
        service_recurrence: o.service_recurrence,
        service_price: o.service_price !== null ? Number(o.service_price) : null,
        price: Number(o.price) || 0,
        setup_fee: Number(o.setup_fee) || 0,
        discount_percent:
          o.discount_percent !== null ? Number(o.discount_percent) : null,
        stripe_price_id: o.stripe_price_id,
        is_active: !!o.is_active,
        sort_order: Number(o.sort_order) || 0,
      }));
    },
  });
}

export function useWlAddonRecurrenceMutations(addonId: string | null) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["wl-addon-recurrence-options", addonId] });

  const upsert = useMutation({
    mutationFn: async (input: {
      id?: string | null;
      payload: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc(
        "wl_upsert_addon_recurrence_option" as never,
        { p_payload: input.payload, p_id: input.id ?? null } as never,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc(
        "wl_delete_addon_recurrence_option" as never,
        { p_id: id } as never,
      );
      if (error) throw error;
      return true;
    },
    onSuccess: invalidate,
  });

  const toggleActive = useMutation({
    mutationFn: async (input: { id: string; active: boolean }) => {
      const { error } = await supabase.rpc(
        "wl_toggle_addon_recurrence_option_active" as never,
        { p_id: input.id, p_active: input.active } as never,
      );
      if (error) throw error;
      return input.active;
    },
    onSuccess: invalidate,
  });

  return { upsert, remove, toggleActive };
}
